import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import {
  BonusRounding,
  PlatformPromotion,
  PlatformPromotionInput,
  PromoStatus,
  PromotablePlatform,
  computePromoBonus,
  getApiErrorMessage,
  platformPromotionsApi,
} from '../api/platformPromotions';

type StatusTab = 'current' | 'ended' | 'all';

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'ended', label: 'Ended' },
  { value: 'all', label: 'All' },
];

const STATUS_CHIP: Record<PromoStatus, { label: string; color: 'success' | 'info' | 'warning' | 'default' }> = {
  live: { label: 'LIVE', color: 'success' },
  scheduled: { label: 'SCHEDULED', color: 'info' },
  disabled: { label: 'PAUSED', color: 'warning' },
  ended: { label: 'ENDED', color: 'default' },
};

const REFRESH_MS = 60_000;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const money = (n: number) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatTime = (iso: string | null) => (iso ? format(new Date(iso), 'MMM d, h:mm a') : 'Never');
const localTimeZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'your local time zone';
  }
})();

const StatusChip: React.FC<{ status: PromoStatus }> = ({ status }) => {
  const chip = STATUS_CHIP[status];
  return (
    <Chip
      size="small"
      color={chip.color}
      label={chip.label}
      icon={
        status === 'live' ? (
          <Box
            component="span"
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor', animation: `${pulse} 1.5s ease-in-out infinite` }}
          />
        ) : undefined
      }
    />
  );
};

const statsLine = (p: PlatformPromotion) =>
  p.stats.loads === 0
    ? '0 loads'
    : `${p.stats.loads} load${p.stats.loads === 1 ? '' : 's'} · ${money(p.stats.loaded_amount)} loaded · ${money(p.stats.bonus_paid)} bonus paid`;

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------

interface PromotionDialogProps {
  open: boolean;
  tenantId: number;
  promotion: PlatformPromotion | null; // null = create
  platforms: PromotablePlatform[];
  rounding: BonusRounding;
  onClose: () => void;
  onSaved: () => void;
}

const PromotionDialog: React.FC<PromotionDialogProps> = ({ open, tenantId, promotion, platforms, rounding, onClose, onSaved }) => {
  const isEdit = promotion !== null;
  const [platformId, setPlatformId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [pct, setPct] = useState('');
  const [cap, setCap] = useState('');
  const [noCap, setNoCap] = useState(false);
  const [startMode, setStartMode] = useState<'now' | 'at'>('now');
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [endMode, setEndMode] = useState<'never' | 'at'>('at');
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    if (promotion) {
      setPlatformId(promotion.platform_id);
      setName(promotion.name || '');
      setPct(String(promotion.bonus_percentage));
      setCap(promotion.max_bonus_per_load != null ? String(promotion.max_bonus_per_load) : '');
      setNoCap(promotion.max_bonus_per_load == null);
      setStartMode('at');
      setStartsAt(new Date(promotion.starts_at));
      setEndMode(promotion.ends_at ? 'at' : 'never');
      setEndsAt(promotion.ends_at ? new Date(promotion.ends_at) : null);
      setIsEnabled(promotion.is_enabled);
    } else {
      setPlatformId('');
      setName('');
      setPct('');
      setCap('');
      setNoCap(false);
      setStartMode('now');
      setStartsAt(null);
      setEndMode('at');
      setEndsAt(null);
      setIsEnabled(true);
    }
  }, [open, promotion]);

  const pctNum = Number(pct);
  const pctValid = pct.trim() !== '' && Number.isFinite(pctNum) && pctNum > 0 && pctNum <= 1000;
  const capNum = noCap || cap.trim() === '' ? null : Number(cap);
  const capValid = noCap || (cap.trim() !== '' && Number.isFinite(capNum) && (capNum as number) > 0);
  const startLocked = isEdit && promotion?.status === 'live';
  const gameName =
    promotion?.platform_display_name || platforms.find((p) => p.id === platformId)?.display_name || 'this game';

  const validate = (): string | null => {
    if (!isEdit && platformId === '') return 'Choose a game.';
    if (name.trim().length > 100) return 'Name must be 100 characters or fewer.';
    if (!pctValid) return 'Bonus % must be greater than 0 and at most 1000.';
    if (!capValid) return 'Enter a max bonus per load greater than 0, or tick "No cap".';
    const now = new Date();
    if (startMode === 'at' && (!startsAt || isNaN(startsAt.getTime()))) return 'Pick a start time.';
    if (endMode === 'at') {
      if (!endsAt || isNaN(endsAt.getTime())) return 'Pick an end time, or choose "Never".';
      const start = startMode === 'at' && startsAt ? startsAt : now;
      if (endsAt <= start) return 'End time must be after start time.';
      if (endsAt <= now) return 'End time must be in the future.';
    }
    return null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const trimmedName = name.trim() || null;
    const capValue = noCap ? null : (capNum as number);
    const endsValue = endMode === 'never' ? null : (endsAt as Date).toISOString();

    setSaving(true);
    setError(null);
    try {
      if (promotion) {
        // Send only what changed.
        const changes: Partial<PlatformPromotionInput> = {};
        if (trimmedName !== (promotion.name || null)) changes.name = trimmedName;
        if (pctNum !== promotion.bonus_percentage) changes.bonus_percentage = pctNum;
        if (capValue !== promotion.max_bonus_per_load) changes.max_bonus_per_load = capValue;
        if (!startLocked && startsAt && startsAt.getTime() !== new Date(promotion.starts_at).getTime()) {
          changes.starts_at = startsAt.toISOString();
        }
        const prevEnds = promotion.ends_at ? new Date(promotion.ends_at).getTime() : null;
        const nextEnds = endsValue ? new Date(endsValue).getTime() : null;
        if (prevEnds !== nextEnds) changes.ends_at = endsValue;
        if (isEnabled !== promotion.is_enabled) changes.is_enabled = isEnabled;
        if (Object.keys(changes).length > 0) {
          await platformPromotionsApi.update(tenantId, promotion.id, changes);
        }
      } else {
        const data: PlatformPromotionInput = {
          platform_id: platformId as number,
          bonus_percentage: pctNum,
          max_bonus_per_load: capValue,
          ends_at: endsValue,
          is_enabled: isEnabled,
        };
        if (trimmedName) data.name = trimmedName;
        if (startMode === 'at' && startsAt) data.starts_at = startsAt.toISOString();
        await platformPromotionsApi.create(tenantId, data);
      }
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save promotion'));
    } finally {
      setSaving(false);
    }
  };

  const previewLine = (amount: number) => {
    const uncapped = computePromoBonus(amount, pctNum, null, rounding);
    const bonus = computePromoBonus(amount, pctNum, noCap ? null : capNum, rounding);
    const capped = bonus < uncapped;
    return { bonus, total: amount + bonus, capped };
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit promotion' : 'New promotion'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {isEdit && promotion?.status === 'live' && (
            <Alert severity="info">Changes apply to new loads only.</Alert>
          )}

          <FormControl fullWidth required disabled={isEdit}>
            <InputLabel id="promo-game-label">Game</InputLabel>
            <Select
              labelId="promo-game-label"
              label="Game"
              value={platformId}
              onChange={(e) => setPlatformId(e.target.value as number | '')}
            >
              {isEdit && promotion && !platforms.some((p) => p.id === promotion.platform_id) && (
                <MenuItem value={promotion.platform_id}>{promotion.platform_display_name}</MenuItem>
              )}
              {platforms.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.display_name || p.name}
                </MenuItem>
              ))}
            </Select>
            {isEdit && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                To change the game, end this promotion and create a new one.
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Optional, e.g. “GW weekend”"
            inputProps={{ maxLength: 100 }}
            fullWidth
          />

          <TextField
            label="Bonus %"
            required
            type="number"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            inputProps={{ min: 0, max: 1000, step: 'any' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            error={pct !== '' && !pctValid}
            helperText={pct !== '' && !pctValid ? 'Greater than 0, at most 1000' : 'Extra bonus on each credit load to this game'}
            fullWidth
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Max bonus per load"
              type="number"
              value={noCap ? '' : cap}
              onChange={(e) => setCap(e.target.value)}
              disabled={noCap}
              inputProps={{ min: 0, step: 'any' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              error={!noCap && cap !== '' && !capValid}
              sx={{ flex: 1 }}
            />
            <FormControlLabel control={<Checkbox checked={noCap} onChange={(e) => setNoCap(e.target.checked)} />} label="No cap" />
          </Stack>

          <FormControl>
            <FormLabel>Starts</FormLabel>
            {!isEdit && (
              <RadioGroup row value={startMode} onChange={(e) => setStartMode(e.target.value as 'now' | 'at')}>
                <FormControlLabel value="now" control={<Radio />} label="Now" />
                <FormControlLabel value="at" control={<Radio />} label="At" />
              </RadioGroup>
            )}
            {startMode === 'at' && (
              <DateTimePicker
                value={startsAt}
                onChange={(d) => setStartsAt(d as Date | null)}
                disabled={startLocked}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mt: 1 } } }}
              />
            )}
          </FormControl>

          <FormControl>
            <FormLabel>Ends</FormLabel>
            <RadioGroup row value={endMode} onChange={(e) => setEndMode(e.target.value as 'never' | 'at')}>
              <FormControlLabel value="never" control={<Radio />} label="Never (until I end it)" />
              <FormControlLabel value="at" control={<Radio />} label="At" />
            </RadioGroup>
            {endMode === 'at' && (
              <DateTimePicker
                value={endsAt}
                onChange={(d) => setEndsAt(d as Date | null)}
                disablePast
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mt: 1 } } }}
              />
            )}
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            Times in {localTimeZone}
          </Typography>

          <FormControlLabel control={<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />} label="Enabled" />

          {pctValid && capValid && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              {[20, 100].map((amount) => {
                const { bonus, total, capped } = previewLine(amount);
                return (
                  <Typography key={amount} variant="body2">
                    A {money(amount)} load on {gameName} → player is charged {money(amount)}, game receives {money(total)} (
                    {money(bonus)} promo bonus{capped ? `, capped at ${money(capNum as number)}` : ''}).
                  </Typography>
                );
              })}
              <Typography variant="caption" color="text.secondary">
                Rounded {rounding} to whole dollars, per your bonus rounding setting.
              </Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ConfirmState = { kind: 'end' | 'delete'; promotion: PlatformPromotion } | null;

const GamePromotionsPage: React.FC<{ tenantId: number }> = ({ tenantId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [statusTab, setStatusTab] = useState<StatusTab>('current');
  const [platformFilter, setPlatformFilter] = useState<number | ''>('');
  const [promotions, setPromotions] = useState<PlatformPromotion[]>([]);
  const [platforms, setPlatforms] = useState<PromotablePlatform[]>([]);
  const [rounding, setRounding] = useState<BonusRounding>('down');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformPromotion | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const loadPromotions = useCallback(
    async (showSpinner = false) => {
      if (!tenantId) return;
      const seq = ++requestSeq.current;
      if (showSpinner) setLoading(true);
      try {
        const data = await platformPromotionsApi.list(tenantId, statusTab, platformFilter === '' ? undefined : platformFilter);
        if (seq === requestSeq.current) setPromotions(data);
      } catch (err) {
        if (seq === requestSeq.current) setError(getApiErrorMessage(err, 'Failed to load promotions'));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [tenantId, statusTab, platformFilter],
  );

  // Initial load + 60 s refresh so LIVE / ENDED flip on their own.
  useEffect(() => {
    loadPromotions(true);
    const timer = setInterval(() => loadPromotions(false), REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadPromotions]);

  useEffect(() => {
    if (!tenantId) return;
    platformPromotionsApi
      .platforms(tenantId)
      .then(setPlatforms)
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load games')));
    platformPromotionsApi
      .bonusRounding(tenantId)
      .then(setRounding)
      .catch(() => setRounding('down'));
  }, [tenantId]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (promotion: PlatformPromotion) => {
    setEditing(promotion);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    setDialogOpen(false);
    setToast('Promotion saved');
    loadPromotions(false);
  };

  const toggleEnabled = async (promotion: PlatformPromotion) => {
    setBusyId(promotion.id);
    setError(null);
    try {
      await platformPromotionsApi.update(tenantId, promotion.id, { is_enabled: !promotion.is_enabled });
      setToast(promotion.is_enabled ? 'Promotion paused' : 'Promotion resumed');
      await loadPromotions(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update promotion'));
    } finally {
      setBusyId(null);
    }
  };

  const openConfirm = (kind: 'end' | 'delete', promotion: PlatformPromotion) => {
    setConfirmError(null);
    setConfirm({ kind, promotion });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirm.kind === 'end') {
        await platformPromotionsApi.end(tenantId, confirm.promotion.id);
        setToast('Promotion ended');
      } else {
        await platformPromotionsApi.remove(tenantId, confirm.promotion.id);
        setToast('Promotion deleted');
      }
      setConfirm(null);
      await loadPromotions(false);
    } catch (err: any) {
      const message = getApiErrorMessage(err, `Failed to ${confirm.kind} promotion`);
      const suggestEnd = confirm.kind === 'delete' && err?.response?.status === 409;
      setConfirmError(suggestEnd ? `${message} Use “End now” instead.` : message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const renderActions = (p: PlatformPromotion) => {
    if (p.status === 'ended') return null;
    const busy = busyId === p.id;
    const canDelete = p.stats.loads === 0 && (p.status === 'scheduled' || p.status === 'disabled');
    const canEnd = p.status === 'live' || p.status === 'disabled';
    return (
      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" onClick={() => openEdit(p)} disabled={busy}>
          Edit
        </Button>
        <Button size="small" variant="outlined" color={p.is_enabled ? 'warning' : 'success'} onClick={() => toggleEnabled(p)} disabled={busy}>
          {p.is_enabled ? 'Pause' : 'Resume'}
        </Button>
        {canEnd && (
          <Button size="small" variant="outlined" color="error" onClick={() => openConfirm('end', p)} disabled={busy}>
            End now
          </Button>
        )}
        {canDelete && (
          <Button size="small" variant="outlined" color="error" onClick={() => openConfirm('delete', p)} disabled={busy}>
            Delete
          </Button>
        )}
      </Stack>
    );
  };

  const isUnfilteredCurrent = statusTab === 'current' && platformFilter === '';

  const renderEmpty = () => (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary" gutterBottom>
        {isUnfilteredCurrent
          ? 'No promotions yet. Run a weekend boost on one game to drive play there.'
          : 'No promotions match this filter.'}
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1 }}>
        New promotion
      </Button>
    </Paper>
  );

  const renderTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Game</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Bonus</TableCell>
            <TableCell>Cap/load</TableCell>
            <TableCell>Starts</TableCell>
            <TableCell>Ends</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {promotions.map((p) => (
            <React.Fragment key={p.id}>
              <TableRow>
                <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>{p.platform_display_name}</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>{p.name || '—'}</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>+{p.bonus_percentage}%</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>
                  {p.max_bonus_per_load != null ? money(p.max_bonus_per_load) : 'No cap'}
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>{formatTime(p.starts_at)}</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>{formatTime(p.ends_at)}</TableCell>
                <TableCell sx={{ borderBottom: 'none' }}>
                  <StatusChip status={p.status} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={7} sx={{ pt: 0 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {statsLine(p)}
                    </Typography>
                    {renderActions(p)}
                  </Stack>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCards = () => (
    <Stack spacing={1.5}>
      {promotions.map((p) => (
        <Card key={p.id} variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography fontWeight={600}>{p.platform_display_name}</Typography>
              <StatusChip status={p.status} />
            </Stack>
            {p.name && (
              <Typography variant="body2" gutterBottom>
                {p.name}
              </Typography>
            )}
            <Typography variant="body2">
              +{p.bonus_percentage}% · {p.max_bonus_per_load != null ? `cap ${money(p.max_bonus_per_load)}/load` : 'no cap'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTime(p.starts_at)} → {formatTime(p.ends_at)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              {statsLine(p)}
            </Typography>
            {renderActions(p)}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Game Promotions
            </Typography>
            <Typography color="text.secondary">
              Give one game an extra bonus for a limited time. Other games are unaffected.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
            New promotion
          </Button>
        </Stack>

        <Accordion disableGutters variant="outlined" sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">How game promotions work</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                The bonus is paid when a player loads credit onto the promoted game, not when they buy credit. A $20 load at
                +50% charges the wallet $20 and the game receives $30.
              </Typography>
              <Typography component="li" variant="body2">
                It stacks with your regular purchase bonus, which is still paid at purchase time.
              </Typography>
              <Typography component="li" variant="body2">
                An optional cap limits the bonus per load. The bonus is rounded to whole dollars using your bonus rounding
                setting.
              </Typography>
              <Typography component="li" variant="body2">
                Only loads from credit balance earn the promo; bonus and loyalty balance loads do not. Other games load as before.
              </Typography>
              <Typography component="li" variant="body2">
                One promotion per game at a time: enabled promotions on the same game can't overlap.
              </Typography>
              <Typography component="li" variant="body2">
                The extra credit comes from your agent balance on that game, so keep enough funds there.
              </Typography>
              <Typography component="li" variant="body2">
                Editing or ending a promotion never takes back a bonus on loads already submitted.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)}>
            {STATUS_TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="promo-game-filter-label">Game</InputLabel>
            <Select
              labelId="promo-game-filter-label"
              label="Game"
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value as number | '')}
            >
              <MenuItem value="">All games</MenuItem>
              {platforms.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.display_name || p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : promotions.length === 0 ? (
          renderEmpty()
        ) : isMobile ? (
          renderCards()
        ) : (
          renderTable()
        )}

        <PromotionDialog
          open={dialogOpen}
          tenantId={tenantId}
          promotion={editing}
          platforms={platforms}
          rounding={rounding}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
        />

        <Dialog open={confirm !== null} onClose={confirmBusy ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{confirm?.kind === 'end' ? 'End promotion' : 'Delete promotion'}</DialogTitle>
          <DialogContent>
            {confirmError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {confirmError}
              </Alert>
            )}
            {confirm?.kind === 'end' ? (
              <Typography>
                End <strong>{confirm.promotion.name || confirm.promotion.platform_display_name}</strong> now? Loads already
                submitted still get their bonus. This can't be undone; create a new promotion to run it again.
              </Typography>
            ) : (
              <Typography>Delete this promotion? It never paid a bonus, so nothing is lost.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)} disabled={confirmBusy}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={runConfirm} disabled={confirmBusy}>
              {confirmBusy ? <CircularProgress size={20} /> : confirm?.kind === 'end' ? 'End now' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={toast !== null} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => setToast(null)} variant="filled">
            {toast}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default GamePromotionsPage;
